import React, { useEffect, ReactElement } from 'react';
import {
  Modal,
  Button,
  Paragraph,
  Spinner,
  CheckCircleIcon,
  ErrorOutlineIcon,
} from 'elmo-elements';
import { useGetImportStatusQuery } from '@/modules/services';
import AiPoweredBadge from '@/components/AiPoweredBadge';

const STEPS = [
  'Uploading PowerPoint',
  'Reading slides',
  'Preparing to generate',
  'Generating slides with AI',
] as const;

type StepState = 'completed' | 'active' | 'failed' | 'pending';

export interface PptConvertingModalProps {
  isOpened: boolean;
  elearningId: string | null;
  totalSlides: number;
  name: string;
  fileName: string;
  uploadError: string | null;
  onDone: (name: string) => void;
  onBackground: (name: string) => void;
  onError: (message: string) => void;
}

const PptConvertingModal = ({
  isOpened,
  elearningId,
  totalSlides,
  name,
  fileName,
  uploadError,
  onDone,
  onBackground,
  onError,
}: PptConvertingModalProps): ReactElement => {
  const isActive = isOpened;
  const isUploading = !elearningId && !uploadError;

  // Single lightweight poll (LMS2-5049): one request replaces the previous
  // findOne + full-frames pair — no frame HTML is transferred during import.
  const { data: status } = useGetImportStatusQuery(elearningId ?? '', {
    skip: !isActive || !elearningId,
    pollingInterval: 5000,
    refetchOnMountOrArgChange: true,
  });

  const frameCount = status?.framesDone ?? 0;
  const importStatus = status?.importStatus;
  const importError = status?.importError;
  // The import POST response provides totalSlides before the first poll lands.
  const slideTotal = status?.totalSlides || totalSlides;
  const progress =
    slideTotal > 0 ? Math.min((frameCount / slideTotal) * 100, 100) : 0;

  const isReady = importStatus === 'ready';
  const isFailed = importStatus === 'failed' || !!uploadError;
  let activeStep: number;
  if (isReady) {
    activeStep = STEPS.length;
  } else if (frameCount > 0) {
    // Slides are being produced; stays active until the import flips to ready.
    activeStep = 3;
  } else if (isUploading || uploadError) {
    activeStep = 0;
  } else if (importStatus === 'importing' || importStatus === 'failed') {
    // A worker has picked the import up (or it died mid-import) but no frames
    // exist yet — screenshotting/pre-generation.
    activeStep = 2;
  } else {
    // 'queued', or the first status poll hasn't landed yet — the upload has
    // just been admitted and is waiting for a worker.
    activeStep = 1;
  }

  const stepState = (index: number): StepState => {
    if (isReady) return 'completed';
    if (uploadError && index === 0) return 'failed';
    if (isFailed && index === activeStep) return 'failed';
    if (index < activeStep) return 'completed';
    if (index === activeStep) return 'active';
    return 'pending';
  };

  const stepIcon = (state: StepState): ReactElement => {
    if (state === 'completed') return <CheckCircleIcon />;
    if (state === 'failed') return <ErrorOutlineIcon />;
    if (state === 'active') return <Spinner size="s" />;
    return <span className="converting-steps__dot" aria-hidden="true" />;
  };

  useEffect(() => {
    if (importStatus === 'ready') {
      onDone(name);
    }
    if (importStatus === 'failed') {
      onError(
        importError ||
          'Conversion failed. Please try again or upload a different file.',
      );
    }
  }, [importStatus, importError, onDone, onError, name]);

  useEffect(() => {
    if (uploadError) {
      onError(uploadError);
    }
  }, [uploadError, onError]);

  return (
    <Modal
      id="PptConvertingModal"
      className="new-module"
      type="large"
      isOpened={isOpened}
      title="Converting your PowerPoint..."
      closeModal={() => onBackground(name)}
      primaryButton={
        <Button
          type="default"
          isUppercase={false}
          onClick={() => onBackground(name)}
        >
          Run in background
        </Button>
      }
    >
      <div className="converting">
        <AiPoweredBadge className="converting__badge" />

        <Paragraph isBold className="converting__filename">
          {fileName}
        </Paragraph>
        <Paragraph size="sm" color="gray">
          {isUploading ? 'Uploading file...' : 'This will take a few minutes'}
        </Paragraph>

        <ol className="converting-steps">
          {STEPS.map((label, index) => {
            const state = stepState(index);
            return (
              <li
                key={label}
                className={`converting-steps__item converting-steps__item--${state}`}
              >
                <span className="converting-steps__icon">
                  {stepIcon(state)}
                </span>
                <span className="converting-steps__label">{label}</span>
              </li>
            );
          })}
        </ol>

        <div className="converting-progress">
          <div
            className="converting-progress__bar"
            role="progressbar"
            aria-label="Conversion progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
          >
            <div
              className="converting-progress__fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <Paragraph size="sm" color="gray" align="center">
            <output>
              {frameCount} of {slideTotal} slides converted
            </output>
          </Paragraph>
        </div>
      </div>
    </Modal>
  );
};

export default PptConvertingModal;
